// =============================================================================
// io/sync/comparison.ts — one week of trades, for the account-comparison brief.
// -----------------------------------------------------------------------------
// A seven-column slice of ONE WEEK. Not the whole book, and that is correct
// rather than a shortcut: the brief's own heading says "this week", and every
// figure in it is a statement about that window.
//
// ── ⚠ WHY IT IS NOT THE ACCOUNTS TAB'S EXISTING READ ──
//
// That read is `MetricsRow` — eleven columns chosen from the set that Class-L
// account numbers actually consume, with no `pattern_id`, no `mistake_tags`,
// no `booleans` and no `annotated`. Those four ARE the brief: without them it
// degenerates into a P&L table the trader can already see on the tab behind it.
//
// Adding them to `METRICS_KEYS` would put a `text[]` and a `jsonb` on every
// account-card read for ever, for columns no card displays. A week is a
// handful of rows.
//
// ── 🚨 A FAILED READ MUST THROW, NOT RETURN AN EMPTY WEEK ──
//
// An empty week and a failed read look identical downstream, and the brief
// built from the first would tell the coach the trader did not trade — which
// the model would then explain, at length, confidently, for real money.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { TRADE_COLUMNS, type TradeColumnKey } from '../../domain/types';
import { PAGE_ROWS } from './load';

export const COMPARISON_KEYS = [
  'accountId', 'date', 'netPnl', 'patternId', 'mistakeTags', 'booleans', 'annotated',
] as const satisfies readonly TradeColumnKey[];

const SELECT = COMPARISON_KEYS.map((k) => TRADE_COLUMNS[k]).join(',');

export interface ComparisonRow {
  accountId: string | null;
  date: string | null;
  netPnl: number | null;
  patternId: string | null;
  mistakeTags: string[];
  booleans: Record<string, boolean>;
  annotated: boolean;
}

export function comparisonRowFromDb(r: Record<string, unknown>): ComparisonRow {
  return {
    accountId: (r['account_id'] as string) ?? null,
    date: (r['date'] as string) ?? null,
    // ⚠ `?? null`, not `|| null` — a $0 trade is a real scratch and the week's
    // trade count includes it.
    netPnl: (r['net_pnl'] as number) ?? null,
    patternId: (r['pattern_id'] as string) ?? null,
    mistakeTags: Array.isArray(r['mistake_tags']) ? (r['mistake_tags'] as string[]) : [],
    booleans: (r['booleans'] && typeof r['booleans'] === 'object')
      ? (r['booleans'] as Record<string, boolean>) : {},
    // 🚨 DEFAULTS TRUE, matching the column's own default and the rest of this
    // client. `annotated === false` means "nobody has reviewed this yet", and
    // reading a missing value as false would report every adherence figure as
    // unknowable on a book where most trades ARE reviewed.
    annotated: r['annotated'] !== false,
  };
}

/** Every trade in `[from, to]`, inclusive, whatever account it is on. */
export async function loadComparisonWeek(
  sb: SupabaseClient, from: string, to: string,
): Promise<ComparisonRow[]> {
  const out: ComparisonRow[] = [];
  for (let page = 0; ; page += 1) {
    const { data, error } = await sb
      .from('trades')
      .select(SELECT)
      .gte('date', from)
      .lte('date', to)
      .order('date', { ascending: true })
      .range(page * PAGE_ROWS, (page + 1) * PAGE_ROWS - 1);

    if (error) {
      throw new Error(
        `loadComparisonWeek: ${error.message}. Refusing to build a coaching brief from a `
        + `partial week — an empty read and a failed one look identical in the brief, and `
        + `the coach would explain a week that did not happen.`,
      );
    }
    const rows = (data ?? []) as unknown as Record<string, unknown>[];
    for (const r of rows) out.push(comparisonRowFromDb(r));
    if (rows.length < PAGE_ROWS) break;
  }
  return out;
}
